/**
 * @typedef {object} Position
 * @property {number} x - The x-coordinate.
 * @property {number} y - The y-coordinate.
 */

/**
 * Manages the state, position, and movement logic for a single character.
 */
export class Character {
    /**
     * The number of update ticks a character waits before moving to the next path segment.
     * @type {number}
     */
    /**
     * The number of game ticks a character waits before moving.
     * The game tick runs every 200ms (5 ticks per second).
     *
     * To convert a desired wait time in seconds to ticks, use this formula:
     *      ticks = seconds * 5
     *
     * --- EXAMPLES (at 5 ticks/sec) ---
     *  - To wait 1 second:   1 * 5 = 5 ticks
     *  - To wait 2 seconds:  2 * 5 = 10 ticks
     *  - To wait 10 seconds: 10 * 5 = 50 ticks
     *
     * --- CALCULATOR ---
     * To have NPCs move every ~2 seconds, set MOVE_COOLDOWN = 10
     */
    static MOVE_COOLDOWN = 10; // Wait ~2 seconds (10 ticks @ 5 ticks/sec) between moves.

    /**
     * @param {object} dependencies - The dependencies to create the character.
     * @param {string|number} dependencies.id - The unique identifier for this character.
     * @param {object} dependencies.config - The configuration object for this character's type.
     * @param {Position} dependencies.position - The initial position of the character.
     */
    constructor({ id, config, position }) {
        /** @type {string|number} */
        this.id = id;
        /** @type {object} */
        this.config = config;
        /** @type {string} */
        this.display = config.display;
        /** @type {Position} */
        this.position = position;

        this.state = {
            isMarkedForRemoval: false,
            isFadingOut: false,
            isNew: true,
            isLeaving: false,
            isChatting: false,
            isSwapping: false,
            currentAction: null,
            isBehavingZanily: false, // Is this NPC the one performing a zany action?
            taggedState: 'none', // 'none', 'correct', 'incorrect'
            actionTimeoutId: null, // New: To track the timeout for the current action
            role: 'wandering',
            interactionTarget: null // A specific settlement to interact with on arrival
        };

        this.movement = {
            path: [],
            cooldown: Math.floor(Math.random() * Character.MOVE_COOLDOWN),
            direction: 'left',
            targetPos: null,
            targetEffectId: null
        };

        /**
         * A map to hold temporary effects on the character, such as speed boosts.
         * @type {Map<string, {key: string, duration: number, [key: string]: any}>}
         */
        this.temporaryEffects = new Map();

        this.attributes = {
            age: 0,
            hunger: 0,
            skinTone: null,
            nickname: null,
            isInLoveWith: null
        };

        this.inventory = {
            accessory: null,
            accessorySide: 'left',
            lastDroppedItem: null
        };

        this.memory = {
            lastVisitedWarehouse: null,
            justVisitedWarehouse: false
        };
    }

    /**
     * Updates the character's state. This should be called on each game tick.
     * It handles the movement cooldown and advances the character along its path.
     */
    update() {
        // --- Update temporary effects ---
        for (const [key, effect] of this.temporaryEffects.entries()) {
            effect.duration--;
            if (effect.duration <= 0) {
                this.temporaryEffects.delete(key);
            }
        }

        if (this.movement.cooldown > 0) {
            this.attributes.age++;
            this.attributes.hunger++;
            this.movement.cooldown--;
            return;
        }

        if (this.movement.path.length > 0) {
            this.position = this.movement.path.shift();

            let moveCooldown = Character.MOVE_COOLDOWN;
            // Check for a speed boost effect
            if (this.temporaryEffects.has('fast_move')) {
                const effect = this.temporaryEffects.get('fast_move');
                moveCooldown = Math.round(Character.MOVE_COOLDOWN / effect.speedMultiplier);
            }
            this.movement.cooldown = moveCooldown;
        }
    }

    /**
     * Sets a new path for the character to follow.
     * @param {Position[]} path - An array of positions, where the first element is the starting point.
     */
    setPath(path) {
        // The path includes the start point, so remove it
        this.movement.path = (path && path.length > 1) ? path.slice(1) : [];
    }

    /**
     * Determines if the character has finished its path and is ready for a new one.
     * @returns {boolean} True if the character needs a new path, otherwise false.
     */
    needsNewPath() {
        return this.movement.path.length === 0 && this.movement.cooldown === 0;

    }
}